import { FormEvent, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import UrgencyBadge, { formatDeadline } from '../components/UrgencyBadge';
import type { ProjectDetail } from './ProjectHome';

type TaskStatus = 'a_faire' | 'en_cours' | 'en_attente' | 'termine';
type Task = {
  id: number;
  project_id: number;
  title: string;
  description: string | null;
  status: TaskStatus;
  progress: string | null;
  urgency: 'normal' | 'urgent' | 'critique';
  deadline: string | null;
  assigned_to: number | null;
  assigned_to_name: string | null;
  created_by_name: string | null;
};

type Comment = {
  id: number;
  content: string;
  user_name: string;
  created_at: string;
};

const COLUMNS: Array<{ key: TaskStatus; label: string }> = [
  { key: 'a_faire', label: 'À faire' },
  { key: 'en_cours', label: 'En cours' },
  { key: 'en_attente', label: 'En attente' },
  { key: 'termine', label: 'Terminé' },
];

export default function ProjectTasks() {
  const { id: projectId } = useParams<{ id: string }>();
  const detail = useOutletContext<ProjectDetail>();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState<'normal' | 'urgent' | 'critique'>('normal');
  const [deadline, setDeadline] = useState('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [selected, setSelected] = useState<Task | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [view, setView] = useState<'kanban' | 'list'>('kanban');

  async function refresh() {
    if (!projectId) return;
    const r = await api.get<{ tasks: Task[] }>(`/projects/${projectId}/tasks`);
    setTasks(r.tasks);
    if (selected) {
      const updated = r.tasks.find((t) => t.id === selected.id);
      if (updated) setSelected(updated);
    }
  }
  useEffect(() => {
    refresh();
  }, [projectId]);

  async function openTask(t: Task) {
    setSelected(t);
    const r = await api.get<{ comments: Comment[] }>(`/tasks/${t.id}/comments`);
    setComments(r.comments);
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    await api.post(`/projects/${projectId}/tasks`, {
      title,
      description: description || null,
      urgency,
      deadline: deadline || null,
      assigned_to: assignedTo ? Number(assignedTo) : null,
    });
    setTitle('');
    setDescription('');
    setDeadline('');
    setCreating(false);
    refresh();
  }

  async function moveTask(t: Task, to: TaskStatus) {
    await api.patch(`/tasks/${t.id}`, { status: to });
    refresh();
  }

  async function postComment() {
    if (!selected || !newComment.trim()) return;
    await api.post(`/tasks/${selected.id}/comments`, { content: newComment.trim() });
    setNewComment('');
    const r = await api.get<{ comments: Comment[] }>(`/tasks/${selected.id}/comments`);
    setComments(r.comments);
  }

  const onDragStart = (e: React.DragEvent, t: Task) => {
    e.dataTransfer.setData('text/plain', String(t.id));
  };
  const onDropTo = (e: React.DragEvent, col: TaskStatus) => {
    e.preventDefault();
    const id = Number(e.dataTransfer.getData('text/plain'));
    const t = tasks.find((x) => x.id === id);
    if (t && t.status !== col) moveTask(t, col);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0 16px' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => setView('kanban')}
            className={`btn btn-ghost ${view === 'kanban' ? 'btn-pill-active' : ''}`}
            style={{ padding: '6px 12px', fontSize: 13 }}
          >
            Kanban
          </button>
          <button
            onClick={() => setView('list')}
            className={`btn btn-ghost ${view === 'list' ? 'btn-pill-active' : ''}`}
            style={{ padding: '6px 12px', fontSize: 13 }}
          >
            Liste
          </button>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Annuler' : '+ Nouvelle tâche'}
        </button>
      </div>

      {creating && (
        <form className="card" onSubmit={onCreate} style={{ marginBottom: 20 }}>
          <div className="field">
            <label>Titre</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="field">
            <label>Description</label>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="field">
              <label>Assigné à</label>
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">— (non assigné)</option>
                {detail.members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Urgence</label>
              <select value={urgency} onChange={(e) => setUrgency(e.target.value as typeof urgency)}>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
                <option value="critique">Critique</option>
              </select>
            </div>
            <div className="field">
              <label>Échéance</label>
              <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </div>
          </div>
          <button className="btn btn-primary">Créer</button>
        </form>
      )}

      {view === 'kanban' ? (
        <div className="kanban">
          {COLUMNS.map((col) => (
            <div
              key={col.key}
              className="kanban-col"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDropTo(e, col.key)}
            >
              <div className="kanban-col-title">
                {col.label}
                <span className="kanban-count">
                  {tasks.filter((t) => t.status === col.key).length}
                </span>
              </div>
              {tasks
                .filter((t) => t.status === col.key)
                .map((t) => (
                  <div
                    key={t.id}
                    className="kanban-card"
                    draggable
                    onDragStart={(e) => onDragStart(e, t)}
                    onClick={() => openTask(t)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div className="card-meta">{t.assigned_to_name ?? '—'}</div>
                      <UrgencyBadge level={t.urgency} />
                    </div>
                    <div className="card-title">{t.title}</div>
                    <div className="card-meta">éch. {formatDeadline(t.deadline)}</div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tasks.map((t) => (
            <div
              key={t.id}
              className="card"
              style={{ cursor: 'pointer' }}
              onClick={() => openTask(t)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{t.title}</strong>
                <UrgencyBadge level={t.urgency} />
              </div>
              <div className="card-meta">
                {t.status} · {t.assigned_to_name ?? 'non assigné'} · éch. {formatDeadline(t.deadline)}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div
          className="modal-backdrop"
          onClick={() => setSelected(null)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="card-meta">Tâche</div>
                <h2 style={{ margin: 0 }}>{selected.title}</h2>
              </div>
              <button className="btn btn-ghost" onClick={() => setSelected(null)}>
                Fermer
              </button>
            </div>
            {selected.description && (
              <p style={{ marginTop: 10 }}>{selected.description}</p>
            )}
            <div className="card-meta">
              Créé par {selected.created_by_name} · assigné à {selected.assigned_to_name ?? '—'} · échéance{' '}
              {formatDeadline(selected.deadline)}
            </div>

            <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
              {COLUMNS.map((c) => (
                <button
                  key={c.key}
                  className={`btn ${selected.status === c.key ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => moveTask(selected, c.key).then(refresh)}
                  style={{ padding: '6px 12px', fontSize: 13 }}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <h3 style={{ marginTop: 18, marginBottom: 8 }}>Commentaires</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {comments.length === 0 && <div className="card-meta">Aucun commentaire.</div>}
              {comments.map((c) => (
                <div
                  key={c.id}
                  style={{ padding: 10, background: 'var(--c-nav)', borderRadius: 6 }}
                >
                  <div className="card-meta">
                    {c.user_name} · {new Date(c.created_at).toLocaleString('fr-FR')}
                  </div>
                  <div>{c.content}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <input
                placeholder="Nouveau commentaire"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                style={{
                  flex: 1,
                  padding: 10,
                  border: '1px solid var(--c-border)',
                  borderRadius: 6,
                }}
              />
              <button className="btn btn-primary" onClick={postComment}>
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
